// src/lib/analytics.js
// Analytics abstraction. FilterProof uses Google Analytics (GA4) as its ONLY
// analytics provider — events are emitted directly via `window.gtag` at the
// call sites (see src/v2/v2Analytics.js, V2Shell, usePipelineV2, and the GA4
// tag in index.html). PostHog was removed 2026-07-06 (never captured in
// production — no key was ever bundled — and was an unwanted paid add-on).
//
// The functions below keep their historical signatures so existing imports
// don't break, but they are safe no-ops: nothing here talks to PostHog, and
// GA4 emission is owned by the explicit gtag call sites. See
// docs/filterproof_analytics_setup.md.

let _initialized = false

// ===== Initialization (no-op; GA4 tag is loaded in index.html) =====
export function initAnalytics() {
  _initialized = true
}

// ===== Core API (no-ops — GA4 is emitted via window.gtag at call sites) =====
// Kept so legacy call sites (App.jsx router tracker, v1 pages) don't break.
export function track() {}
export function identify() {}
export function group() {}
export function pageview() {}
export function setUserProperties() {}
export function reset() {}
export function flush() { return Promise.resolve() }

// Compat aliases
export const setUser = identify
export const setProps = setUserProperties
export const page = pageview

// ===== Event Constants (still referenced by call sites) =====
export const AnalyticsEvents = Object.freeze({
  // Page views
  VIEW_HOME: 'view_home',
  VIEW_LOGIN: 'view_login',
  VIEW_ACCOUNT: 'view_account',
  VIEW_SUBMIT: 'view_submit',
  VIEW_BLOG: 'view_blog',

  // Actions
  CLICK_ANALYZE: 'click_analyze',
  CLICK_ENHANCE: 'click_enhance',
  CLICK_RESEARCH: 'click_research',
  CHAT_MESSAGE_SENT: 'chat_message_sent',

  // Authentication
  SIGN_UP_STARTED: 'sign_up_started',
  SIGN_UP_COMPLETE: 'sign_up_complete',
  SIGN_IN_COMPLETE: 'sign_in_complete',

  // Billing
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_COMPLETED: 'checkout_completed',
  BILLING_PORTAL_OPENED: 'billing_portal_opened',

  // Features
  TRUTH_MODE_TOGGLED: 'truth_mode_toggled',
  SKILL_GAP_VIEWED: 'skill_gap_viewed',
  VARIANT_GENERATED: 'variant_generated',
  FIX_IT_RUN: 'fix_it_run',
  METRICS_ENHANCED: 'metrics_enhanced',

  // Lead capture
  LEAD_CAPTURED: 'lead_captured',
  LEAD_CONVERTED: 'lead_converted',
  PDF_REQUESTED: 'pdf_requested',
  PDF_DOWNLOADED: 'pdf_downloaded',
  ATS_REPORT_REQUESTED: 'ats_report_requested',
  ATS_REPORT_SUCCESS: 'ats_report_success',
  ATS_REPORT_FAILED: 'ats_report_failed',

  // A/B Testing
  VARIANT_EXPOSED: 'variant_exposed',
  VARIANT_ASSIGNED: 'ab_variant_assigned',
  EXPOSURE: 'ab_exposure',
  CONVERSION: 'ab_conversion',

  // Conversion assists
  UPGRADE_NUDGE_SHOWN: 'upgrade_nudge_shown',
  UPGRADE_NUDGE_CLICKED: 'upgrade_nudge_clicked',
  UPGRADE_CTA_VIEWED: 'upgrade_cta_viewed',
  UPGRADE_CTA_CLICKED: 'upgrade_cta_clicked',
  BASIC_REPORT_DOWNLOAD_CLICKED: 'basic_report_download_clicked',
  ONE_TIME_UNLOCK_CTA_VIEWED: 'one_time_unlock_cta_viewed',
  ONE_TIME_UNLOCK_CTA_CLICKED: 'one_time_unlock_cta_clicked',

  // One-time checkout / post-checkout recovery
  ONE_TIME_CHECKOUT_STARTED: 'one_time_checkout_started',
  ONE_TIME_CHECKOUT_COMPLETED: 'one_time_checkout_completed',
  ONE_TIME_CHECKOUT_RECOVERED: 'one_time_checkout_recovered',
  ONE_TIME_REPORT_UNLOCKED: 'one_time_report_unlocked',
  ONE_TIME_REPORT_RESTORE_FAILED: 'one_time_report_restore_failed',
  CHECKOUT_SUCCESS_PAGE_VIEW: 'checkout_success_page_view',
  CHECKOUT_SUCCESS_REPORT_LOADED: 'checkout_success_report_loaded',

  // Generic
  CLICK: 'ui_click',
  PAGEVIEW: 'pageview',
  ERROR: 'app_error',
  APP_INITIALIZED: 'app_initialized',
})

// ===== Default export =====
export default track
